// ---------------------------------------------------------------------------
// OverviewTab (US4 / PRODOPSAI-404)
//
// Replaces the Phase-0 `<EmptyTabPlaceholder phase={2} />` mounted in the
// `overview` slot of `ManagementViewPage`. Composes the two cards shipped in
// US2 + US3 into the Overview tab's two-column layout:
//
//   - Left column (~2/3): `HighlightsCard`, fed by `useOverviewHighlights`
//     (filter-driven, refetches on Scope/Window/Cadence change).
//   - Right column (~1/3): `MaturityCard`, self-contained — owns its own data
//     fetch via `useFamilyMaturity` and is filter-INDEPENDENT.
//
// The 4-state pattern (loading / error / empty / data) lives only on the
// highlights side; failures or in-flight requests on that side do NOT blank
// the maturity card. This matches the spec's "error isolation per side"
// requirement (US4 AC) and mirrors `metric-view/TrendCard.tsx:42-55` for the
// loading/error/empty/data branch ordering.
//
// Filter source-of-truth is `useManagementViewFilters` (Phase 0 US2) — this
// component does NOT read `useSearchParams` directly.
// ---------------------------------------------------------------------------

import { ErrorBanner, Spinner } from '@/components/ui';
import { useManagementViewFilters } from '@/hooks/use-management-view-filters';
import { useOverviewHighlights } from '@/hooks/use-overview-highlights';
import { HighlightsCard } from './HighlightsCard';
import { MaturityCard } from './MaturityCard';
import type { IOverviewHighlightsResponse } from '@skystone/shared';

// ---------------------------------------------------------------------------
// State branches — one per render branch, each kept tiny and ordered exactly
// like `TrendCard.tsx:42-55` so the canonical 4-state pattern is obvious.
// Kept at module scope per SonarCloud rule S7721 (no helper components
// defined inside a component render body).
// ---------------------------------------------------------------------------

function HighlightsLoadingBranch(): JSX.Element {
  return (
    <div
      className="flex h-40 items-center justify-center"
      data-testid="overview-highlights-loading"
    >
      <Spinner size="lg" />
    </div>
  );
}

interface HighlightsErrorBranchProps {
  message: string;
}

function HighlightsErrorBranch({ message }: Readonly<HighlightsErrorBranchProps>): JSX.Element {
  return (
    <div data-testid="overview-highlights-error">
      <ErrorBanner message={message} />
    </div>
  );
}

interface HighlightsDataBranchProps {
  data: IOverviewHighlightsResponse;
}

function HighlightsDataBranch({ data }: Readonly<HighlightsDataBranchProps>): JSX.Element {
  return (
    <HighlightsCard
      highlights={data.highlights}
      periodLabel={data.periodLabel}
    />
  );
}

interface HighlightsSideProps {
  loading: boolean;
  error: string | null;
  data: IOverviewHighlightsResponse | null;
}

/**
 * Render the highlights side of the Overview layout. Picks one of:
 *   - loading branch (spinner)
 *   - error branch (ErrorBanner)
 *   - data branch (HighlightsCard — its own logic handles empty `highlights`)
 *
 * Empty-state copy is owned by `HighlightsCard` (US2), so this side has no
 * dedicated empty branch — it falls through to the data branch which renders
 * the card with the empty-state body.
 */
function HighlightsSide({ loading, error, data }: Readonly<HighlightsSideProps>): JSX.Element {
  if (loading) return <HighlightsLoadingBranch />;
  if (error) return <HighlightsErrorBranch message={error} />;
  if (data) return <HighlightsDataBranch data={data} />;
  // Defensive fallback — `useApi` always populates one of the three states
  // above on first render, so this branch is unreachable in practice.
  return <HighlightsLoadingBranch />;
}

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------

export function OverviewTab(): JSX.Element {
  const { scope, window: windowFilter, cadence, includeArchived } = useManagementViewFilters();
  const { data, loading, error } = useOverviewHighlights({
    scope,
    window: windowFilter,
    cadence,
    includeArchived,
  });

  return (
    <div
      className="grid grid-cols-1 gap-6 lg:grid-cols-[2fr_1fr]"
      data-testid="overview-tab"
    >
      <div data-testid="overview-highlights-column">
        <HighlightsSide loading={loading} error={error} data={data} />
      </div>
      <div data-testid="overview-maturity-column">
        <MaturityCard />
      </div>
    </div>
  );
}

export default OverviewTab;
